import { httpService } from '@flexo/general';
import { createAsyncThunk } from '@reduxjs/toolkit';
import * as ALERT from '../alert-reducer/alerts.reducer';
import {
  AdminUser,
  AdminPaginationState,
  Community,
  EntitiesData,
  Member,
  StructuredData,
} from './interfaces';

const ENV = (import.meta as any).env;

export function structureData(data: EntitiesData): any {
  const communityMap = new Map<string, Community | any>();
  const memberMap = new Map<string, Member | any>();
  const assignedMemberIds = new Set<string>();
  const assignedSiteIds = new Set<string>();
  const assignedEvIds = new Set<string>();
  const assignedWallboxIds = new Set<string>();

  if (!data || Object.keys(data)?.length === 0) {
    return;
  }

  const today = new Date().toISOString().slice(0, 10);

  // Resolve every community a member currently belongs to.
  // A member can own sites across multiple communities, so the v1_4 changelog
  // (one entry per community assignment) is authoritative — the flat
  // `communityId` scalar is first-write-wins and collapses multi-community
  // members to a single, arbitrary community. Falls back to the scalar only
  // when no changelog is present (pre-v1_4 payloads).
  function getMemberCommunityIds(member: any): Set<string> {
    const ids = new Set<string>();
    const changelog = Array.isArray(member?.changelog) ? member.changelog : [];

    for (const entry of changelog) {
      // Currently in the community = window covers today: already started
      // (open start or startDate <= today) AND not yet left (open end or
      // endDate >= today). Mirrors resolveAtDate — a future-dated assignment
      // must NOT surface the member before they actually join.
      const started = !entry.startDate || entry.startDate <= today;
      const notEnded = !entry.endDate || entry.endDate >= today;
      if (entry?.communityId && started && notEnded) {
        ids.add(entry.communityId);
      }
    }

    // Trust the flat scalar ONLY when there is no changelog at all (pre-v1_4
    // payloads). If a changelog exists but yields no active community — the
    // member has left everywhere — we must not re-add the stale scalar, which
    // would resurrect a departed membership in the current directory.
    if (changelog.length === 0 && member?.communityId) {
      ids.add(member.communityId);
    }

    return ids;
  }

  // The community a site currently belongs to (a site is in one community at a
  // time): the v1_4 changelog entry covering today, else the flat scalar.
  function siteCurrentCommunityId(site: any): string | null {
    const changelog = Array.isArray(site?.changelog) ? site.changelog : [];

    for (const entry of changelog) {
      const started = !entry.startDate || entry.startDate <= today;
      const notEnded = !entry.endDate || entry.endDate >= today;
      if (entry?.communityId && started && notEnded) {
        return entry.communityId;
      }
    }

    return site?.communityId ?? null;
  }

  // Create community map
  (data.communities || data.community).forEach((community) => {
    communityMap.set(community.communityId, {
      ...community,
      members: [] as any,
    });
  });

  // Create member map
  (data.members || data.member).forEach((member) => {
    memberMap.set(member.memberId, {
      ...member,
      sites: [],
      evs: [],
      wallboxes: [],
    });

    // Associate the member with EVERY community it currently belongs to
    // (not just the single scalar communityId — see getMemberCommunityIds).
    getMemberCommunityIds(member).forEach((communityId) => {
      if (!communityMap.has(communityId)) return;
      const community = communityMap.get(communityId);
      if (!community.members.some((m: any) => m.memberId === member?.memberId)) {
        community.members.push(memberMap.get(member?.memberId));
        assignedMemberIds?.add(member?.memberId);
      }
    });
  });

  // Associate sites with members and track assigned site IDs
  (data.sites || data.site).forEach((site: any) => {
    if (memberMap.has(site.memberId)) {
      const member = memberMap.get(site.memberId);
      member.sites.push(site);
      assignedSiteIds.add(site.siteExternalId);

      // Associate members with communities via sites if not already associated
      if (!member.communityId && communityMap.has(site.communityId)) {
        const community = communityMap.get(site.communityId);
        if (!community.members.some((m) => m.memberId === member.memberId)) {
          community.members.push(member);
          assignedMemberIds.add(member.memberId);
        }
      }
    }
  });

  // Associate EVs with members and track assigned EV IDs
  data?.ev.forEach((ev) => {
    if (memberMap.has(ev.memberId)) {
      const member = memberMap.get(ev.memberId);
      member.evs.push(ev);
      assignedEvIds.add(ev.evId);

      // Associate members with communities via EVs if not already associated
      if (!member.communityId && communityMap.has(ev.communityId)) {
        const community = communityMap.get(ev.communityId);
        if (!community.members.some((m) => m.memberId === member.memberId)) {
          community.members.push(member);
          assignedMemberIds.add(member.memberId);
        }
      }
    }
  });

  // Associate Wallboxes with members and track assigned Wallbox IDs
  data?.wallbox.forEach((wallbox) => {
    if (memberMap.has(wallbox.memberId)) {
      const member = memberMap.get(wallbox.memberId);
      member.wallboxes.push(wallbox);
      assignedWallboxIds.add(wallbox.wallboxId);

      // Associate members with communities via Wallboxes if not already associated
      if (!member.communityId && communityMap.has(wallbox.communityId)) {
        const community = communityMap.get(wallbox.communityId);
        if (!community.members.some((m) => m.memberId === member.memberId)) {
          community.members.push(member);
          assignedMemberIds.add(member.memberId);
        }
      }
    }
  });

  // Structure the data
  const structuredData: StructuredData = {
    communities: [],
    unassignedMembers: [],
    unassignedSites: [],
    unassignedEVs: [],
    unassignedWallboxes: [],
  };

  communityMap.forEach((community) => {
    // A member nested under a community shows only that community's sites.
    // The member object is shared by reference across every community it
    // belongs to and accumulates ALL its sites, so scope each community's copy
    // to the sites that currently live in it. Community-context consumers
    // (total-sites-card, search) rely on this. Member-context consumers that
    // need the member's full footprint (e.g. the member overview) aggregate
    // sites across the member's per-community copies instead — see
    // information-card-content-member-label-value and templates helpers.
    structuredData.communities.push({
      ...community,
      members: community.members.map((member: any) => ({
        ...member,
        sites: (member.sites || []).filter(
          (site: any) => siteCurrentCommunityId(site) === community.communityId
        ),
      })),
    });
  });

  memberMap.forEach((member) => {
    if (!assignedMemberIds.has(member.memberId)) {
      structuredData.unassignedMembers.push(member);
    }
  });

  data?.sites?.forEach((site) => {
    if (!assignedSiteIds.has(site.siteExternalId)) {
      structuredData.unassignedSites.push(site);
    }
  });

  data?.ev?.forEach((ev) => {
    if (!assignedEvIds.has(ev.evId)) {
      structuredData.unassignedEVs.push(ev);
    }
  });

  data?.wallbox?.forEach((wallbox) => {
    if (!assignedWallboxIds.has(wallbox.wallboxId)) {
      structuredData.unassignedWallboxes.push(wallbox);
    }
  });

  return structuredData;
}

export const SetSelectedEntityAsync = createAsyncThunk(
  'metadata/SetSelectedEntityAsync',
  async function (data: any) {
    return {
      ...data,
    };
  }
);

// ⚠️ MOCK FLAG — set to false before committing
export const MOCK_ENTITIES = false;

export const SetEntitiesAsync = createAsyncThunk(
  'metadata/SetEntitiesAsync',
  async function (data: any) {
    const { dispatch } = data;

    if (MOCK_ENTITIES) {
      try {
        const mockUrl = `${window.location.origin}/entities.mock.json`;
        console.log('[MOCK] Fetching from:', mockUrl);
        const res = await fetch(mockUrl);
        const contentType = res.headers.get('content-type') || '';
        console.log('[MOCK] Response status:', res.status, 'content-type:', contentType);
        if (!contentType.includes('json')) {
          const text = await res.text();
          console.error('[MOCK] Expected JSON but got:', text.substring(0, 200));
          throw new Error('Mock response is not JSON');
        }
        const mockData = await res.json();
        console.log('[MOCK] Loaded entities, keys:', Object.keys(mockData));
        const entities = {
          ...mockData,
          sites: mockData.site,
          communities: mockData.community,
          members: mockData.member,
        };
        return {
          entities,
          structuredEntities: entities ? structureData(entities) : null,
        };
      } catch (err) {
        console.error('[MOCK] Failed to load entities.mock.json', err);
      }
    }

    // Token auto-injected by httpService interceptor
    const [entities] = await Promise.all([
      httpService
        .get({
          url: `${ENV.VITE_APP_BASE_URL}/community/catalog/v1_4/entities`,
          mock: false,
        })
        .then((res: any) => {
          if (res.status === 200) {
            // TO DO: remove this atrocious work around

            return {
              ...res.data,
              sites: res.data.site,
              communities: res.data.community,
              members: res.data.member,
            };
          } else {
            // console.warn({
            //   status: res.status,
            //   message: res.statusText,
            // });

            dispatch(
              ALERT.SetAlert({
                value: {
                  type: 'BANNER_SESSION_EXPIRED',
                },
              } as any)
            );

            return null;
          }
        })
        .catch((err: any) => {
          dispatch(
            ALERT.SetAlert({
              value: {
                type: 'BANNER_UNEXPECTED_ERROR',
              },
            } as any)
          );

          return null;
        }),
    ]);

    return {
      entities,
      structuredEntities: entities ? structureData(entities) : null,
    };
  }
);

export const SetMembersAsync = createAsyncThunk(
  'metadata/SetMembersAsync',
  async function (data: any) {
    const { dispatch, COMMUNITY_ID } = data;

    // Token auto-injected by httpService interceptor
    const [members] = await Promise.all([
      httpService
        .get({
          url: `${ENV.VITE_APP_BASE_URL}/community/catalog/v1_4/community/${COMMUNITY_ID}/members`,
          // /community/catalog/v1_4/community/{communityId}/members
        })
        .then((res: any) => {
          if (res.status === 200) {
            return res.data;
          } else {
            // console.warn({
            //   status: res.status,
            //   message: res.statusText,
            // });
            return null;
          }
        })
        .catch((err: any) => {
          // console.warn(err);

          dispatch(
            ALERT.SetAlert({
              value: {
                type: 'BANNER_UNEXPECTED_ERROR',
              },
            } as any)
          );

          return null;
        }),
    ]);

    return { members };
  }
);

/**
 * Transform API user response to AdminUser format for the frontend tables.
 */
function transformApiUserToAdmin(apiUser: any): AdminUser {
  const communityDetails = Array.isArray(apiUser.communities)
    ? apiUser.communities
    : Array.isArray(apiUser.communityDetails)
      ? apiUser.communityDetails
      : [];

  const formatDate = (dateStr: string | null | undefined): string => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return '';
      const dd = String(date.getDate()).padStart(2, '0');
      const mm = String(date.getMonth() + 1).padStart(2, '0');
      const yy = String(date.getFullYear()).slice(-2);
      return `${dd}/${mm}/${yy}`;
    } catch {
      return '';
    }
  };

  // Map role to display name
  const roleDisplayMap: Record<string, string> = {
    administrator: 'Admin',
    supervisor: 'Community Supervisor',
    enduser: 'End User',
  };

  return {
    userId: apiUser.userId || null,
    clerkUserId: apiUser.clerkUserId || null,
    user:
      `${apiUser.firstName || ''} ${apiUser.lastName || ''}`.trim() ||
      apiUser.email ||
      'Unknown',
    email: apiUser.email || '',
    role: roleDisplayMap[apiUser.role] || apiUser.role || 'Unknown',
    state: apiUser.status === 'active' ? 'active' : 'invited',
    activated:
      apiUser.status === 'active' ? formatDate(apiUser.activatedAt) : '',
    invited: formatDate(apiUser.invitedAt),
    expiry: formatDate(apiUser.expiresAt),
    // Show reminder only if user was reinvited (has reinvitedAt)
    reminder: apiUser.reinvitedAt
      ? `Reminder: ${formatDate(apiUser.reinvitedAt)}`
      : undefined,
    reinvitedAt: apiUser.reinvitedAt || undefined, // Store raw timestamp for updates
    communities:
      apiUser.role === 'administrator'
        ? 'All'
        : String(communityDetails.length || 0),
    _raw: {
      firstName: apiUser.firstName || '',
      lastName: apiUser.lastName || '',
      phone: apiUser.phone,
      address: apiUser.address,
      city: apiUser.city,
      zipcode: apiUser.zipcode,
      source: apiUser.source,
      communityDetails,
      reinvitedAt: apiUser.reinvitedAt || undefined,
      expiresAt: apiUser.expiresAt || undefined,
    },
  };
}

export const SetAdminsAsync = createAsyncThunk(
  'metadata/SetAdminsAsync',
  async function (data: { dispatch: any; organizationId: string; isRefresh?: boolean }) {
    const { dispatch, organizationId } = data;

    const admins = await httpService
      .get({
        // Exclude end users - they are managed elsewhere, not in admin panel
        // v1_4 endpoint - same path, same response format
        url: `${ENV.VITE_APP_BASE_URL}/community/catalog/v1_4/organization/${organizationId}/users?exclude_roles=enduser`,
        mock: false,
      })
      .then((res: any) => {
        if (res.status === 200) {
          const apiUsers = res.data?.users || [];
          return apiUsers.map(transformApiUserToAdmin);
        } else {
          dispatch(
            ALERT.SetAlert({
              value: {
                type: 'BANNER_SESSION_EXPIRED',
              },
            } as any)
          );
          return null;
        }
      })
      .catch((err: any) => {
        console.error('Failed to fetch admin users:', err);
        dispatch(
          ALERT.SetAlert({
            value: {
              type: 'BANNER_UNEXPECTED_ERROR',
            },
          } as any)
        );
        return null;
      });

    return { admins };
  }
);

/**
 * Server-side paginated admin users fetch.
 * Each call targets a specific status (active or invited) for independent table pagination.
 */
export interface SetAdminsPaginatedParams {
  dispatch: any;
  organizationId: string;
  status: 'active' | 'invited';
  page?: number;
  pageSize?: number;
  search?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  isRefresh?: boolean;
  /** When true, appends results to existing list instead of replacing */
  isLoadMore?: boolean;
}

export const SetAdminsPaginatedAsync = createAsyncThunk(
  'metadata/SetAdminsPaginatedAsync',
  async function (data: SetAdminsPaginatedParams) {
    const {
      dispatch,
      organizationId,
      status,
      page = 1,
      pageSize = 25,
      search,
      sortBy = status === 'active' ? 'user' : 'invited',
      sortDir = status === 'active' ? 'asc' : 'desc',
    } = data;

    const params = new URLSearchParams();
    params.set('exclude_roles', 'enduser');
    params.set('page', String(page));
    params.set('pageSize', String(pageSize));
    params.set('status', status);
    params.set('sortBy', sortBy);
    params.set('sortDir', sortDir);
    if (search) params.set('search', search);

    const result = await httpService
      .get({
        url: `${ENV.VITE_APP_BASE_URL}/community/catalog/v1_4/organization/${organizationId}/users?${params.toString()}`,
        mock: false,
      })
      .then((res: any) => {
        if (res.status === 200) {
          const apiUsers = res.data?.users || [];
          const pagination = res.data?.pagination || {
            page: 1,
            pageSize: 25,
            totalItems: 0,
            totalPages: 1,
          };
          return {
            users: apiUsers.map(transformApiUserToAdmin),
            pagination,
          };
        } else {
          dispatch(
            ALERT.SetAlert({
              value: { type: 'BANNER_SESSION_EXPIRED' },
            } as any)
          );
          return null;
        }
      })
      .catch((err: any) => {
        console.error('Failed to fetch paginated admin users:', err);
        dispatch(
          ALERT.SetAlert({
            value: { type: 'BANNER_UNEXPECTED_ERROR' },
          } as any)
        );
        return null;
      });

    return { result, status };
  }
);

// Helper to format ISO date to dd/mm/yy
function formatDateDdMmYy(isoDate: string): string {
  const date = new Date(isoDate);
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const yy = String(date.getFullYear()).slice(-2);
  return `${dd}/${mm}/${yy}`;
}

export const ResendInvitationAsync = createAsyncThunk(
  'metadata/ResendInvitationAsync',
  async function (data: {
    dispatch: any;
    organizationId: string;
    userId: string;
    locale: string;
    language: string;
    inviteUrl: string;
  }) {
    const { dispatch, organizationId, userId, locale, language, inviteUrl } =
      data;

    // v1_4 endpoint: POST /organization/{id}/users/{clerkUserId}/invitations
    const result = await httpService
      .post({
        url: `${ENV.VITE_APP_BASE_URL}/community/catalog/v1_4/organization/${organizationId}/users/${userId}/invitations`,
        data: {
          locale,
          language,
          inviteUrl,
        },
        mock: false,
      })
      .then((res: any) => {
        // Check for success: status 200 OR response data indicates success
        const isSuccess = res.status === 200 || res.data?.success === true;

        if (isSuccess) {
          const { reinvitedAt, newExpiresAt } = res.data || {};

          // Update only this user's reminder and expiry in the store (no full refresh)
          // Original invite date should remain the same, only update reminder with reinvite date
          if (reinvitedAt && newExpiresAt) {
            dispatch({
              type: 'metadata/UpdateAdminInvitation',
              payload: {
                userId,
                newInvited: null, // Keep original invite date unchanged
                newExpiry: formatDateDdMmYy(newExpiresAt),
                newExpiresAtRaw: newExpiresAt, // Raw ISO date for expiry filtering
                newReinvitedAt: reinvitedAt, // Store the reinvited timestamp
              },
            });
          }

          dispatch(
            ALERT.SetAlert({
              value: {
                type: 'BANNER_RESEND_INVITATION_SUCCESS',
              },
            } as any)
          );
          return { success: true };
        } else {
          dispatch(
            ALERT.SetAlert({
              value: {
                type: 'BANNER_UNEXPECTED_ERROR',
              },
            } as any)
          );
          return { success: false };
        }
      })
      .catch((err: any) => {
        console.error('Failed to resend invitation:', err);
        dispatch(
          ALERT.SetAlert({
            value: {
              type: 'BANNER_UNEXPECTED_ERROR',
            },
          } as any)
        );
        return { success: false };
      });

    return result;
  }
);

export const DisableUserAsync = createAsyncThunk(
  'metadata/DisableUserAsync',
  async function (data: {
    dispatch: any;
    organizationId: string;
    userId: string;
  }) {
    const { dispatch, organizationId, userId } = data;

    // v1_4 endpoint: DELETE /organization/{id}/users/{clerkUserId}
    const result = await httpService
      .delete({
        url: `${ENV.VITE_APP_BASE_URL}/community/catalog/v1_4/organization/${organizationId}/users/${userId}`,
        mock: false,
      })
      .then((res: any) => {
        console.log('DisableUserAsync response:', res.status, res.data);
        if (res.status === 200) {
          dispatch(
            ALERT.SetAlert({
              value: {
                type: 'BANNER_USER_DISABLED_SUCCESS',
                message: 'User disabled successfully',
              },
            } as any)
          );
          // Remove user from store directly (no API call needed)
          // Using type string to avoid circular dependency with metatada.reducer.ts
          dispatch({ type: 'metadata/RemoveAdmin', payload: userId });
          dispatch({ type: 'metadata/RemoveAdminPaginated', payload: userId });
          return { success: true, user: res.data?.user };
        } else {
          console.log('DisableUserAsync non-200 response, showing error');
          dispatch(
            ALERT.SetAlert({
              value: {
                type: 'BANNER_UNEXPECTED_ERROR',
              },
            } as any)
          );
          return { success: false };
        }
      })
      .catch((err: any) => {
        console.error('DisableUserAsync caught error:', err);
        console.error(
          'Error response:',
          err?.response?.status,
          err?.response?.data
        );
        dispatch(
          ALERT.SetAlert({
            value: {
              type: 'BANNER_UNEXPECTED_ERROR',
            },
          } as any)
        );
        return { success: false };
      });

    return result;
  }
);
